import { DeepPartial, ISeriesApi, LogicalRange } from 'lightweight-charts'
import {
    PaneConfig,
    Series,
    SeriesData,
    SeriesGroup as SeriesGroupType,
    SeriesOptions,
    SeriesSourceOptions
} from './chart.typings'
import Chart from './chart'
import {
    DataUpdateRequestType,
    getIndicatorDrawingSettings,
    getPriceScaleId,
    getViewportCandleInfo
} from '../../util/chart'
import { debounce } from 'throttle-debounce'
import { SeriesGroupCategory } from '../../pages/home'
import { BLANK_SERIES_ID, VOLUME_SCALE } from '../../assets/constants'
import { ChartSubType, ChartType } from '../../proto/generated/chart'
import DrawingBase from './drawingTools/drawing.base'
import { DrawingDisplayType, DrawingSettingsMap } from './drawingTools/drawing.interface'
import BaseChartSeries from './chartSeries/baseSeries'
import CandleStickSeries from './chartSeries/candleStickSeries'
import LineSeries from './chartSeries/lineSeries'
import BarSeries from './chartSeries/barSeries'
import AreaSeries from './chartSeries/areaSeries'
import FillSeries from './chartSeries/fillSeries'
import HistogramSeries from './chartSeries/histogramSeries'
import HeikinAshiSeries from './chartSeries/heikinAshiSeries'
import BaselineSeries from './chartSeries/baselineSeries'
import HollowCandleSeries from './chartSeries/hollowCandleSeries'
import HighLowCandleSeries from './chartSeries/highLowCandleSeries'
import HlcAreaSeries from './chartSeries/hlcAreaSeries'
import RenkoSeries from './chartSeries/renkoSeries'
import KagiSeries from './chartSeries/kagiSeries'
import PandFSeries from './chartSeries/pandFSeries'
import LineBreakSeries from './chartSeries/lineBreakSeries'
import VolumeCandleSeries from './chartSeries/volumeCandleSeries'
import StepLineSeries from "./chartSeries/stepLineSeries";
import LineWithMarkersSeries from './chartSeries/lineWithMarkersSeries';

export type SeriesGroupOptions = {
    config: SeriesGroupType,
    series: Series[]
    pane: PaneConfig,
    paneIndex: number,
    primary: boolean,
    chart: Chart

}

export default class SeriesGroup {
    config: SeriesGroupType
    series: Series[]
    pane: PaneConfig
    paneIndex: number
    primary: boolean
    chart: Chart
    seriesDrawings: Record<string, DrawingBase<any, any, any>> = {}
    comparableModeIndexStart = -1
    chartSeries: Record<string, BaseChartSeries> = {}
    constructor(options: SeriesGroupOptions) {
        this.config = options.config
        this.series = options.series
        this.pane = options.pane
        this.paneIndex = options.paneIndex
        this.primary = options.primary
        this.chart = options.chart
    }

    async initialize() {
        this.createAllSeries()
    }

    reset() { }

    updateConfig(options: SeriesGroupOptions) {
        this.config = options.config
        this.series = options.series
        this.pane = options.pane
        this.paneIndex = options.paneIndex
        this.primary = options.primary
        this.chart = options.chart
        this.onConfigUpdate()
    }

    onConfigUpdate() { }
    getSeriesSource(): SeriesSourceOptions['source'] {
        return "close"
    }
    getChartSeries(type: ChartType, seriesConfig: Series, paneIndex: number, options: any, chartSubType?: ChartSubType): BaseChartSeries {
        switch (type) {
            case ChartType.CANDLESTICK:
                return new CandleStickSeries(this.chart, seriesConfig, this.config, paneIndex, options)
            case ChartType.LINE:
                return new LineSeries(this.chart, seriesConfig, this.config, paneIndex, options)
            case ChartType.BAR:
                return new BarSeries(this.chart, seriesConfig, this.config, paneIndex, options)
            case ChartType.AREA:
                return new AreaSeries(this.chart, seriesConfig, this.config, paneIndex, options)
            case ChartType.FILL:
                return new FillSeries(this.chart, seriesConfig, this.config, paneIndex, options);
            case ChartType.HISTOGRAM:
                return new HistogramSeries(this.chart, seriesConfig, this.config, paneIndex, options)
            case ChartType.HEIKIN_ASHI:
                return new HeikinAshiSeries(this.chart, seriesConfig, this.config, paneIndex, options);
            case ChartType.HOLLOW_CANDLES:
                return new HollowCandleSeries(this.chart, seriesConfig, this.config, paneIndex, options);
            case ChartType.HIGH_LOW:
                return new HighLowCandleSeries(this.chart, seriesConfig, this.config, paneIndex, options);
            case ChartType.BASELINE:
                return new BaselineSeries(this.chart, seriesConfig, this.config, paneIndex, options);
            case ChartType.HLC_AREA:
                return new HlcAreaSeries(this.chart, seriesConfig, this.config, paneIndex, options)
            case ChartType.RENKO:
                return new RenkoSeries(this.chart, seriesConfig, this.config, paneIndex, options)
            case ChartType.KAGI:
                return new KagiSeries(this.chart, seriesConfig, this.config, paneIndex, options);
            case ChartType.POINT_AND_FIGURE:
                return new PandFSeries(this.chart, seriesConfig, this.config, paneIndex, options);
            case ChartType.LINE_BREAK:
                return new LineBreakSeries(this.chart, seriesConfig, this.config, paneIndex, options);
            case ChartType.VOLUME_CANDLESTICK:
                return new VolumeCandleSeries(this.chart, seriesConfig, this.config, paneIndex, options);
            case ChartType.STEP_LINE_CHART:
                return new StepLineSeries(this.chart, seriesConfig, this.config, paneIndex, options);
            case ChartType.LINE_WITH_MARKERS:
                return new LineWithMarkersSeries(this.chart, seriesConfig, this.config, paneIndex, options);
            default:
                return new CandleStickSeries(this.chart, seriesConfig, this.config, paneIndex, options)
        }
    }

    createAllSeries(primaryOnly = false) {
        const blankSeriesId = this.series[1]?.id ?? BLANK_SERIES_ID;
        this.series.forEach((series) => {
            if (primaryOnly && series.id === blankSeriesId) return;
            this.createSeries(series);
        })

        if (this.config.primary) {
            this.chartSeries[blankSeriesId]?.seriesApi.applyOptions({ visible: false })
        }
    }
    getSeriesOptions(chartType: ChartType, config: Series): DeepPartial<SeriesOptions> {
        const { id, priceFormat, visibility = true, options } = config
        const { category, outputValues, scale, compareMode } = this.config

        return {
            priceLineVisible: category === SeriesGroupCategory.SERIES_SECURITY,
            visible: visibility,
            priceScaleId:
                category === SeriesGroupCategory.SERIES_VOLUME
                    ? VOLUME_SCALE
                    : this.chart.overlayPriceScale ? "OVERLAY_PRICE_SCALE" : getPriceScaleId(scale, this.chart.defaultPriceScaleId),
            pane: this.paneIndex,
            priceFormat: {
                type: priceFormat
            },
            ...options
        }
    }
    createSeries(config: Series) {
        const { id, chartType, drawing, chartSubType } = config
        const {
            drawingType = undefined,
            drawingSettings = undefined
        } = this?.config?.outputValues?.[config.id] ?? {}
        if (!this.chart.chartApi) return
        if (!drawing) {
            this.chartSeries[id] = this.getChartSeries(chartType, config, this.paneIndex, this.getSeriesOptions(chartType, config), chartSubType)
        } else if (drawingType) {
            const pSg = this.chart?.primarySeriesGroup
            const dr = this.chart.drawingManager.addDrawing(drawingType as keyof DrawingSettingsMap, DrawingDisplayType.INDICATOR, {
                drawingType: drawingType,
                persist: false
            }, {
                ...getIndicatorDrawingSettings(drawingType as keyof DrawingSettingsMap, this.chart.chartApi.options(), drawingSettings ?? {})

            }, undefined, undefined, pSg?.pane?.id, pSg?.series?.[0]?.id)
            if (dr !== null) {
                this.seriesDrawings[config.id] = dr!
            }
        }
    }

    recreateSeries(id: string) {
        const series = this.series.find(s => s.id === id);
        if (!series) return;
        const chartSeries = this.chartSeries[id];

        if (chartSeries) {
            const data = chartSeries.getRawData()
            chartSeries.destroy();
            delete this.chartSeries[id];
            this.createSeries(series)
            this.chartSeries[id]?._setData(data)
        }
    }

    getDrawingIds(): string[] {
        if (!this.chart) {
            return []
        }
        return Object.values(this.seriesDrawings)?.map((sd) => sd?.id)
    }

    getSeriesIdOfDrawingId(drawingId: string): string | undefined {
        if (!this.chart) {
            return undefined
        }
        let seriesId: string | undefined = undefined
        Object.entries(this.seriesDrawings)?.forEach((entry) => {
            if (entry[1]?.id === drawingId) {
                seriesId = entry[0]
            }
        })
        return seriesId
    }

    onVisibleLogicalRangeChange = debounce(100, (range: LogicalRange | null) => {
        const chartSeries = this.chartSeries[this.series[0].id]
        if (!range) return
        const barsInfo = chartSeries.getBarsInfo(range)
        if (!barsInfo) return

        const viewportCandleInfo = getViewportCandleInfo(range, barsInfo, chartSeries.getData())
        this.chart.onVisibleLogicalRangeChange(viewportCandleInfo)
    })

    updateData = (newData: boolean, updateType?: DataUpdateRequestType) => {
        const { securityList, category, primary } = this.config
        let key = securityList[0]
        let data = this.chart.getRawData()
        const { id } = this.series[0]
        switch (category) {
            case SeriesGroupCategory.SERIES_SECURITY: {
                if (!newData) return
                if (!data.length) {
                    this.chartSeries[id]._setData([])
                    const blankSeriesId = this.series[1]?.id ?? BLANK_SERIES_ID
                    this.chartSeries[blankSeriesId]?._setData([])
                    return
                }
                if (primary) {
                    const blankSeriesId = this.series[1]?.id ?? BLANK_SERIES_ID;
                    if (updateType === DataUpdateRequestType.LIVE) {
                        const lastCandle = data[data.length - 1];
                        this.chartSeries[id]._update(lastCandle);

                        if (!this.chart.noWhitespace) {
                            let emptyCandle = this.chart.emptyTimeSlots[0];
                            let c = 0;
                            while (emptyCandle && emptyCandle.time < lastCandle.time) {
                                c += 1;
                                emptyCandle = this.chart.emptyTimeSlots[c];
                            }

                            if (c > 0) {
                                this.chart.emptyTimeSlots = this.chart.emptyTimeSlots.slice(c);
                                this.chartSeries[blankSeriesId]?._setData(this.chart.emptyTimeSlots);
                            }
                        } else {
                            this.chartSeries[blankSeriesId]?._update(lastCandle);
                        }
                    } else {
                        if (!this.chart.noWhitespace) {
                            this.chartSeries[blankSeriesId]?._setData(this.chart.emptyTimeSlots);
                        } else {
                            this.chartSeries[blankSeriesId]?._setData(data);
                        }
                        this.chartSeries[id]._setData(data);
                    }

                }
                break
            }

            case SeriesGroupCategory.SERIES_COMPARABLE_SECURITY: {
                data = this.chart.getData(key) || []
                const primaryData = this.chart.getRawData()

                if (!data.length || !primaryData.length) {
                    this.chartSeries[id]._setData([])
                    return
                }
                if (updateType === DataUpdateRequestType.LIVE) {
                    const lastCandle = data[data.length - 1]
                    this.chartSeries[id]._update(lastCandle)
                } else if (updateType === DataUpdateRequestType.INITIAL) {
                    this.chartSeries[id]._setData(data)
                } else {
                    this.chartSeries[id]._setData(data)
                }
            }

        }
    }

    getDrawing(): DrawingBase<any, any, any> | null {
        if (this.config.category !== SeriesGroupCategory.SERIES_DRAWING) {
            return null
        }
        return Object.values(this.seriesDrawings)?.[0] ?? null
    }

    getPrimarySeries(): BaseChartSeries | undefined {
        if (!this.config.primary) return
        const { id } = this.series[0]
        return this.chartSeries[id]
    }

    getPrimarySeriesApi(): ISeriesApi<any> | undefined {
        if (!this.config.primary) return

        const { id } = this.series[0]
        return this.chartSeries[id]?.seriesApi
    }

    removeSeries(id: string) {
        const series = this.chartSeries[id]
        if (!series) return
        series.destroy()
        delete this.chartSeries[id]
    }

    removeAllSeries(primaryOnly = false) {
        const blankSeriesId = this.series[1]?.id ?? BLANK_SERIES_ID;

        this.series.forEach(({ id, drawing }) => {
            if (primaryOnly && id === blankSeriesId) return;

            if (drawing) {
                this.removeDrawing(id)
            } else {
                this.removeSeries(id)
            }
        })
    }

    removeDrawing(id: string) {
        const dr = this.seriesDrawings[id]
        if (dr) {
            dr.destroy()
        }
        delete this.seriesDrawings[id]
    }

    getSeriesData(offset: number): Record<string, SeriesData[]> {
        const data: Record<string, SeriesData[]> = {}
        const { securityList, category, primary } = this.config
        let key = securityList[0]
        this.series.forEach(({ id }) => {
            let seriesData: SeriesData[] = []
            seriesData = this.chartSeries[id]?.getData();

            if (!seriesData) {
                data[id] = []
                return
            }

            const index = seriesData.length - (offset || 1) - 2
            if (index + 1 < 0) {
                data[id] = seriesData ? [seriesData[0]] : []
            } else {
                data[id] = seriesData ? seriesData.slice(index + 1, index + 3) : []
            }
        })

        return data
    }

    toggleVisibility(visible: boolean) {
        const blankId = this.series[1]?.id ?? BLANK_SERIES_ID
        Object.values(this.chartSeries).forEach((series) => {
            if (series.config.id === blankId) return;
            series.seriesApi.applyOptions({
                visible
            })
        })
    }

    refreshGroup(recreate = true, primaryOnly = false) {
        if (recreate) {
            this.removeAllSeries(primaryOnly)
            this.createAllSeries()
            this.updateData(true, DataUpdateRequestType.INITIAL)
            // Reattach overlay price scale if this is the primary series group
            if (this.primary) {
                this.chart.attachOverlayPriceScale()
            }
        } else {
            const blankId = this.series[1]?.id ?? BLANK_SERIES_ID
            this.series.forEach((series) => {
                if (series.id === blankId) return;
                const chartSeries = this.chartSeries[series.id]
                chartSeries?.updateConfig(series);
                chartSeries?.updateOptions(this.getSeriesOptions(series.chartType, series))
            })
        }
    }

    refreshCompareMode() {
        this.config.compareMode = this.chart.compareMode
    }

    subscribeVisibleLogicalRangeChange() {
        if (this.chart.destroyed || !this.chart.chartApi) return
        this.chart.chartApi.timeScale().subscribeVisibleLogicalRangeChange(this.onVisibleLogicalRangeChange)
    }

    unsubscribeVisibleLogicalRangeChange() {
        if (this.chart.destroyed || !this.chart.chartApi) return
        this.chart.chartApi.timeScale().unsubscribeVisibleLogicalRangeChange(this.onVisibleLogicalRangeChange)
    }

    destroy(pushToHistory = false) {
        this.removeAllSeries()
        this.unsubscribeVisibleLogicalRangeChange()
    }
}